import type { LedgerSummary } from '@cuito/types';
import { AMOUNT_UNAVAILABLE_TEXT, formatServerYuan } from '../../lib/schemeForm';

interface Props {
  summary: LedgerSummary;
}

function Card({
  title,
  value,
  unit,
  hint,
  valueTestId,
  testId,
}: {
  title: string;
  value: string;
  unit?: string;
  hint: string;
  valueTestId: string;
  testId: string;
}) {
  return (
    <article className="flex flex-col gap-1 rounded-lg border-2 border-black p-4" data-testid={testId}>
      <span className="text-sm text-gray-500">{title}</span>
      <span className="flex items-baseline gap-1">
        {/*
          值节点**只放纯数字/纯金额**，单位单独一个节点：验收脚本要用它和服务端的
          `summary.orderCount` 逐字交叉核对（Chain D 的「真浏览器段 DOM 契约」：
          `ledger-summary-orders` 的文本 = 纯数字）。
        */}
        <span className="text-xl font-bold" data-testid={valueTestId}>
          {value}
        </span>
        {unit !== undefined && <span className="text-sm text-gray-500">{unit}</span>}
      </span>
      <span className="text-xs text-gray-600">{hint}</span>
    </article>
  );
}

/**
 * 汇总卡片（契约 §13.3；UI 规范 §3.2 `/ledger`：订单数 / 件数 / 金额 / 已退）。
 *
 * **不做任何减法**：`netQty` / `netAmount` 是服务端算好的派生量（§13.3 原文：
 * 「由服务端算好下发，前端做减法就会出现第二份口径」）。金额一律走
 * `formatServerYuan`（→ `@cuito/ui` 的 `formatYuan`，§7.8 的千分位）；
 * 读不出来的金额说「金额以服务端为准」，绝不说成 `¥0.00`（附录 B1s 的教训）。
 */
export default function LedgerSummaryCards({ summary }: Props) {
  const gross = formatServerYuan(summary.grossAmount) ?? AMOUNT_UNAVAILABLE_TEXT;
  const refunded = formatServerYuan(summary.refundedAmount) ?? AMOUNT_UNAVAILABLE_TEXT;
  const net = formatServerYuan(summary.netAmount) ?? AMOUNT_UNAVAILABLE_TEXT;

  return (
    <section className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="ledger-summary">
      <Card
        title="订单数"
        value={String(summary.orderCount)}
        unit="单"
        hint={`${summary.lineCount} 行`}
        valueTestId="ledger-summary-orders"
        testId="ledger-card-orders"
      />
      <Card
        title="件数"
        value={String(summary.netQty)}
        unit="件"
        hint={`售出 ${summary.grossQty} 件`}
        valueTestId="ledger-summary-qty"
        testId="ledger-card-qty"
      />
      <Card
        title="金额"
        value={net}
        hint={`毛额 ${gross}`}
        valueTestId="ledger-summary-amount"
        testId="ledger-card-amount"
      />
      <Card
        title="已退"
        value={String(summary.refundedQty)}
        unit="件"
        hint={refunded}
        valueTestId="ledger-summary-refunded"
        testId="ledger-card-refunded"
      />
    </section>
  );
}
